
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowTopRightOnSquareIcon,
  LockClosedIcon,
} from "@heroicons/vue/24/outline";
import { processClasses, SimplifiedPlannerProcess } from "@oculai/planner";
import { format, intervalToDuration } from "date-fns";
import { defineComponent, PropType } from "vue";
import OculaiVideoPlayer from "@/components/other/OculaiVideoPlayer.vue";
import Tooltip from "@/components/tooltips/TooltipBasic.vue";
import { useHasProjectFeature } from "@/composables/project";
import OpsProcessesRepository from "@/repositories/OpsProcessesRepository";
import logger from "@/services/logger";
import processDurationService from "@/services/processDurationService";
import { ShortenedProcess } from "@/types/Process";

export const encodedLabelsToDecodedLabel = processClasses.reduce((acc, processClass) => {
  acc[processClass.encodedLabel] = processClass.decodedLabel;

  return acc;
}, {} as Record<string, string>);

export default defineComponent({
  name: "ProcessSideBarContent",
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    processes: {
      type: Array as PropType<Array<SimplifiedPlannerProcess & { name?: string }>>,
      required: true,
    },
  },
  emits: ["closed"],
  components: {
    OculaiVideoPlayer,
    Tooltip,
    ChevronLeftIcon,
    ChevronRightIcon,
    ArrowTopRightOnSquareIcon,
    LockClosedIcon,
  },
  data() {
    return {
      videoUrls: {} as Record<string, string>,
      videoUrlLoading: false as boolean,
      selectedProcessIndex: 0 as number,
      processTabSkip: 0 as number,
      hasDailyReportPermission: false as boolean,
    };
  },
  mounted() {
    if (this.open) {
      this.updateHasDailyReportPermission();
      this.selectProcess(this.processes[0]);
    }
  },
  setup() {
    const hasWorkingHoursFeature = useHasProjectFeature("working_hours");
    return {
      hasWorkingHoursFeature,
    };
  },
  watch: {
    open(value) {
      if (value) {
        this.updateHasDailyReportPermission();
        this.processTabSkip = 0;
        this.selectProcess(this.processes[0]);
      }
    },
    processes(value) {
      if (value.length > 0) {
        this.selectProcess(value[0]);
      }
    },
  },

  computed: {
    visibleTabCount() {
      return 2;
    },
    selectedProcess() {
      return this.processes[this.selectedProcessIndex];
    },
    hideWorkingHours() {
      return !this.hasWorkingHoursFeature;
    },
  },
  methods: {
    goTabLeft() {
      if (this.selectedProcessIndex > 0) {
        this.selectedProcessIndex -= 1;
        if (this.selectedProcessIndex < this.processTabSkip) {
          this.processTabSkip -= 1;
        }
      }
    },
    goTabRight() {
      if (this.selectedProcessIndex < this.processes.length - 1) {
        this.selectedProcessIndex += 1;
        if (this.selectedProcessIndex >= this.processTabSkip + this.visibleTabCount) {
          this.processTabSkip += 1;
        }
      }
    },
    selectProcess(process: SimplifiedPlannerProcess & { name?: string }) {
      this.selectedProcessIndex = this.processes.indexOf(process);
      this.loadProcessVideoUrl();
    },
    loadProcessVideoUrl() {
      if (!this.hasPermission("all_camera_features_user")) {
        this.videoUrlLoading = false;
        return;
      }

      if (!this.selectedProcess) {
        this.videoUrlLoading = false;
        return;
      }
      const process = this.selectedProcess;
      if (this.videoUrls[process._id]) {
        this.videoUrlLoading = false;
        return;
      }
      this.videoUrlLoading = true;
      const { customer_name, site_id } = this.$route.params;

      return OpsProcessesRepository.loadProcessVideoUrl(
        customer_name as string,
        site_id as string,
        process._id,
      )
        .then(async ({ url }) => {
          this.videoUrls[process._id] = url;
        })
        .catch((error) => {
          if (error?.response?.status !== 404) {
            logger.error(error);
          }
        })
        .finally(() => {
          if (this.selectedProcess === process) {
            this.videoUrlLoading = false;
          }
        });
    },
    getProcessLabel(encodedLabel: number) {
      return encodedLabelsToDecodedLabel[encodedLabel];
    },
    formatDateDisplay(date: Date) {
      return format(date, "dd.MM.yyyy");
    },
    formatDate(date: Date | null | undefined | string) {
      if (typeof date === "string") {
        return date;
      }
      return date ? format(date, "yyyy-MM-dd") : "";
    },
    formatTime(time: Date) {
      return format(time, "HH:mm");
    },
    getFormattedDuration(startTime: Date, endTime: Date) {
      const duration = intervalToDuration({
        start: endTime,
        end: startTime,
      });
      const durationFormattedHours = (duration.hours || 0).toString().padStart(2, "0");
      const durationFormattedMinutes = (duration.minutes || 0).toString().padStart(2, "0");

      return `${durationFormattedHours}:${durationFormattedMinutes}h`;
    },
    updateHasDailyReportPermission() {
      this.hasDailyReportPermission = this.hasPermission("daily_report_user");
    },
    calculateWorkHoursSum(process: ShortenedProcess) {
      return processDurationService.calculateWorkHoursSum([process], true);
    },
  },
});
